import { DateTime } from 'luxon'

/** A local calendar day, 'yyyy-MM-dd'. Raw model date strings are parsed into it at the boundary and never compared raw. */
declare const isoBrand: unique symbol
export type IsoDate = string & { readonly [isoBrand]: true }

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/
// Model timestamps without a zone ('2026-09-23 09:39:31') are already local.
const LOCAL_STAMP = /^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/

const fromDateTime = (d: DateTime): IsoDate | null => (d.isValid ? (d.toISODate() as IsoDate) : null)

/** Parse any date or timestamp string a model carries into the local calendar day. null for empty or invalid input. */
export function toIsoDate(raw: string | null | undefined): IsoDate | null {
  if (!raw) return null
  const s = raw.trim()
  if (DAY_ONLY.test(s)) return fromDateTime(DateTime.fromISO(s))
  const local = LOCAL_STAMP.exec(s)
  if (local) return fromDateTime(DateTime.fromISO(local[1]))
  if (s.includes('/')) return fromDateTime(DateTime.fromFormat(s.split(/[ ,]/)[0], 'M/d/yyyy'))
  return fromDateTime(DateTime.fromISO(s))
}

export const todayIso = (): IsoDate => DateTime.now().toISODate() as IsoDate

const dt = (d: IsoDate) => DateTime.fromISO(d)

/** Sunday of the week containing d (weeks start Sunday, like today's schedule). */
export function weekStartOf(d: IsoDate): IsoDate {
  const day = dt(d)
  return day.minus({ days: day.weekday % 7 }).toISODate() as IsoDate
}

export function addDays(d: IsoDate, n: number): IsoDate {
  return dt(d).plus({ days: n }).toISODate() as IsoDate
}

/** "10/23" */
export function md(d: IsoDate): string {
  return dt(d).toFormat('M/d')
}

/** "10/23/2026", the selected_date format the editors parse. */
export function mdy(d: IsoDate): string {
  return dt(d).toFormat('M/d/yyyy')
}

/** "Today · Fri, Oct 23" | "Yesterday · Thu, Oct 22" | "Wed, Oct 21" */
export function dayLabel(d: IsoDate, today: IsoDate): string {
  const label = dt(d).toFormat('ccc, LLL d')
  if (d === today) return `Today · ${label}`
  if (d === addDays(today, -1)) return `Yesterday · ${label}`
  return label
}

/** "Oct 23" */
export function monthDay(d: IsoDate): string {
  return dt(d).toFormat('LLL d')
}

/** "Oct 23, 2026" */
export function monthDayYear(d: IsoDate): string {
  return dt(d).toFormat('LLL d, yyyy')
}

/** Whole days from `from` to `to`; negative when `to` is already past. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round(dt(to).diff(dt(from), 'days').days)
}

/** "Friday · 10/23/2026" */
export function longDay(d: IsoDate): string {
  return dt(d).toFormat("cccc '·' M/d/yyyy")
}

/** "Oct 18 – 24" | "Sep 27 – Oct 3" */
export function weekLabel(weekStart: IsoDate): string {
  const a = dt(weekStart)
  const b = a.plus({ days: 6 })
  return a.month === b.month ? `${a.toFormat('LLL d')} – ${b.toFormat('d')}` : `${a.toFormat('LLL d')} – ${b.toFormat('LLL d')}`
}
